// =============================================================================
// MeuDriver — DataPortabilityCard (Batch S4)
// Seção de portabilidade de dados (LGPD Art. 18) para uso em perfis
// =============================================================================

import { useState } from 'react'
import { Download, FileJson, Loader2, CheckCircle, Clock, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  useDataExportRequests,
  useRequestDataExport,
  useGetMyData,
  downloadJsonBlob,
  type DataExportStatus,
} from '@/hooks/useDataPortability'

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<DataExportStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending:    { label: 'Pendente',      variant: 'secondary' },
  processing: { label: 'Processando',  variant: 'default' },
  ready:      { label: 'Pronto',        variant: 'default' },
  delivered:  { label: 'Entregue',     variant: 'outline' },
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DataPortabilityCard() {
  const { data: requests = [], isLoading } = useDataExportRequests()
  const requestExport = useRequestDataExport()
  const getMyData     = useGetMyData()

  const [isDownloading, setIsDownloading] = useState(false)

  // Última requisição ativa
  const latestRequest = requests[0] ?? null
  const hasPending    = latestRequest?.status === 'pending' || latestRequest?.status === 'processing'

  async function handleRequestExport() {
    try {
      await requestExport.mutateAsync()
      toast.success('Solicitação de exportação criada. Você pode baixar seus dados agora.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar solicitação.'
      toast.error(msg)
    }
  }

  async function handleDownload() {
    setIsDownloading(true)
    try {
      const data = await getMyData.mutateAsync()
      const filename = `meudriver-dados-${format(new Date(), 'yyyy-MM-dd')}.json`
      downloadJsonBlob(data, filename)
      toast.success('Download iniciado!')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao baixar dados.'
      toast.error(msg)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileJson className="h-4 w-4 text-primary" />
          Meus dados (LGPD Art. 18)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Você tem o direito de acessar e portar todos os seus dados pessoais armazenados na plataforma,
          conforme a Lei Geral de Proteção de Dados (LGPD, Art. 18).
        </p>

        {/* Histórico de solicitações */}
        {!isLoading && requests.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Solicitações anteriores
            </p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {requests.slice(0, 5).map((req) => {
                const cfg = STATUS_CONFIG[req.status]
                return (
                  <div
                    key={req.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {req.status === 'delivered' || req.status === 'ready'
                        ? <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                        : <Clock className="h-3.5 w-3.5 text-amber-500" />
                      }
                      {format(parseISO(req.created_at), "d 'de' MMM 'de' yyyy", { locale: ptBR })}
                    </div>
                    <Badge variant={cfg.variant} className="text-[10px] px-2 py-0.5">
                      {cfg.label}
                    </Badge>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex flex-wrap gap-3 pt-1">
          {/* Botão solicitar */}
          {!hasPending && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRequestExport}
              disabled={requestExport.isPending}
              className="gap-2"
              data-testid="request-data-export-btn"
            >
              {requestExport.isPending
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Registrando...</>
                : <><RefreshCw className="h-3.5 w-3.5" />Registrar solicitação</>
              }
            </Button>
          )}

          {hasPending && (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              Solicitação em andamento...
            </div>
          )}

          {/* Botão baixar */}
          <Button
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading || getMyData.isPending}
            className="gap-2"
            data-testid="download-my-data-btn"
          >
            {isDownloading || getMyData.isPending
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Gerando arquivo...</>
              : <><Download className="h-3.5 w-3.5" />Baixar meus dados</>
            }
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          O arquivo baixado contém seus dados em formato JSON: perfil, histórico de aulas,
          avaliações, certificados e endereços salvos.
        </p>
      </CardContent>
    </Card>
  )
}
